import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MessageCircle, ArrowRight, Sparkles, Lightbulb } from "lucide-react";

const tips = [
  {
    emoji: "🎯",
    text: "You gained 15 new customers this week — send them a quick thank-you SMS."
  },
  {
    emoji: "📅",
    text: "Friday campaigns get 2× more responses — try a weekend offer."
  },
  {
    emoji: "☕",
    text: "Your Café Latte Combo is trending — add a limited-time add-on."
  }
];

interface AskEmmaCardProps {
  onOpenChat: () => void;
}

export function AskEmmaCard({ onOpenChat }: AskEmmaCardProps) {
  const [currentTip, setCurrentTip] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [showBlink, setShowBlink] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentTip((prev) => (prev + 1) % tips.length);
        setIsAnimating(false);
      }, 300);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  // Blinking animation
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setShowBlink(true);
      setTimeout(() => setShowBlink(false), 200);
    }, 4000);

    return () => clearInterval(blinkInterval);
  }, []);

  // Simulate thinking state periodically
  useEffect(() => {
    const thinkInterval = setInterval(() => {
      setIsThinking(true);
      setTimeout(() => setIsThinking(false), 2000);
    }, 15000);

    return () => clearInterval(thinkInterval);
  }, []);

  return (
    <Card
      className="relative overflow-hidden bg-gradient-to-r from-[#EAF8F5] to-white border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center gap-6 p-6">
        {/* Avatar Section - 38% */}
        <div className="flex-shrink-0 w-[38%] flex items-center justify-center relative">
          <div className="relative">
            {/* Thinking sparkle */}
            {isThinking && (
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 animate-bounce">
                <Sparkles className="w-6 h-6 text-primary animate-pulse" />
              </div>
            )}

            {/* Generating lightbulb */}
            {isAnimating && (
              <div className="absolute -top-8 right-0 animate-pulse">
                <Lightbulb className="w-5 h-5 text-yellow-500 fill-yellow-200" />
              </div>
            )}

            {/* Animated chat bubble */}
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center animate-pulse group-hover:scale-110 transition-transform duration-300">
              <MessageCircle className="w-4 h-4 text-primary" />
            </div>

            {/* Emma Avatar with animations */}
            <div
              className={`relative transition-all duration-500 ${
                isHovered ? "scale-110 rotate-2" : "scale-100"
              } ${isThinking ? "animate-bounce" : ""}`}
            >
              {/* Pulsing glow - enhanced on hover */}
              <div
                className={`absolute inset-0 bg-primary/10 rounded-full blur-xl transition-all duration-500 ${
                  isHovered
                    ? "opacity-90 scale-110 bg-primary/20"
                    : "opacity-50 animate-pulse"
                }`}
              />

              {/* Avatar image */}
              <div className="relative">
                <img
                  src="/emma.png"
                  alt="Emma - Your Business Assistant"
                  className={`relative w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg transition-all duration-300 ${
                    showBlink ? "brightness-90" : "brightness-100"
                  }`}
                />

                {/* Wave hand animation on hover */}
                {isHovered && (
                  <div className="absolute bottom-2 right-2 text-2xl animate-[wave_0.5s_ease-in-out_infinite]">
                    👋
                  </div>
                )}
              </div>
            </div>

            {/* Typing bubble (appears when thinking) */}
            {isThinking && (
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-full px-4 py-2 shadow-lg animate-fade-in">
                <div className="flex gap-1">
                  <div
                    className="w-2 h-2 bg-primary/60 rounded-full animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  />
                  <div
                    className="w-2 h-2 bg-primary/60 rounded-full animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  />
                  <div
                    className="w-2 h-2 bg-primary/60 rounded-full animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  />
                </div>
              </div>
            )}

            {/* Hover tooltip */}
            {isHovered && (
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs px-3 py-1 rounded-full whitespace-nowrap animate-fade-in shadow-lg">
                Ask me anything about your store
              </div>
            )}

            {/* Decorative ambient glows */}
            <div className="absolute -bottom-2 -left-2 w-16 h-16 bg-primary/5 rounded-full blur-lg animate-pulse" />
            <div
              className="absolute -top-2 -right-8 w-12 h-12 bg-primary/5 rounded-full blur-lg animate-pulse"
              style={{ animationDelay: "1s" }}
            />
          </div>
        </div>

        {/* Content Section - 62% */}
        <div className="flex-1 space-y-3">
          <div className="space-y-1">
            <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
              Ask Emma 🤖
            </h3>
            <p className="text-sm text-muted-foreground">
              Got a question or need ideas? Emma's here to share smart, simple
              tips to help your business grow.
            </p>
          </div>

          {/* Dynamic Tip */}
          <div className="relative min-h-[48px] flex items-center">
            <div
              className={`transition-opacity duration-300 ${
                isAnimating ? "opacity-0" : "opacity-100"
              }`}
            >
              <div className="flex items-start gap-3 p-3 bg-white/60 backdrop-blur-sm rounded-lg border border-primary/10">
                <span className="text-xl flex-shrink-0">
                  {tips[currentTip].emoji}
                </span>
                <p className="text-sm text-foreground/90 leading-relaxed">
                  {tips[currentTip].text}
                </p>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <Button
            onClick={onOpenChat}
            className="bg-[#009688] hover:bg-[#00796B] text-white shadow-[0_4px_12px_rgba(0,150,136,0.25)] hover:shadow-[0_6px_20px_rgba(0,150,136,0.4)] transition-all rounded-xl px-[18px] py-[10px] gap-2.5 text-[15px] font-semibold min-h-[44px]"
          >
            Ask Emma
            <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>

      {/* Decorative gradient overlay with subtle animation */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/3 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      {/* Ambient glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none animate-pulse" />
    </Card>
  );
}
